import { api } from './client';
import type { Page } from './catalogs';

/** Податковий розрахунок J0500110 (об'єднана звітність ПДФО/ВЗ/ЄСВ). */

export type TaxReportType = 'REPORTING' | 'REPORTING_NEW' | 'CORRECTING';
export type TaxPeriodKind = 'MONTH' | 'QUARTER';

export interface TaxReport {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  report_type: TaxReportType;
  period_kind: TaxPeriodKind;
  period_year: number;
  period_month: number | null;
  period_quarter: number | null;
  report_ordinal: number;
  appendices: string[];
  kved: string | null;
  prof_risk_class: number | null;
  sti_name: string | null;
  posted: boolean;
  deletion_mark: boolean;
  created_at: string;
}

export interface TaxReportCreate {
  organization_id: string;
  report_type?: TaxReportType;
  period_kind?: TaxPeriodKind;
  period_year: number;
  period_month?: number | null;
  period_quarter?: number | null;
  appendices?: string[];
  manual_only?: boolean;
}

export interface TaxReportPersonRow {
  last_name: string;
  first_name: string;
  middle_name: string | null;
  inn: string;
  income_sign: string;
  gross: number;
  esv: number;
  pdfo: number;
  wz: number;
  days: number;
}

export interface TaxReportPreview {
  org: { name: string; edrpou: string | null };
  totals: {
    gross: number; esv_base: number; esv: number;
    pdfo: number; wz: number; insured_count: number;
  };
  persons: TaxReportPersonRow[];
}

/** Form-view: форма-бланк (рядки з офіційними назвами + правки). */
export interface TaxFormScalar {
  code: string;
  num: string | null;
  label: string;
  value: string | null;
  auto: string | null;
  is_manual: boolean;
  editable: boolean;
  kind: 'text' | 'date';
}
export interface TaxFormCell {
  value: string | null;
  auto: string | null;
  is_manual: boolean;
}
export interface TaxFormColumn {
  tag: string;
  num: string | null;
  label: string;
  kind: 'text' | 'date';
}
export interface TaxFormTable {
  key?: string;
  columns: TaxFormColumn[];
  rows: { row_num: number; cells: Record<string, TaxFormCell> }[];
}
export interface TaxFormView {
  code: string;
  title: string;
  scalars: TaxFormScalar[];
  table: TaxFormTable | null;
  tables?: TaxFormTable[];
}

export interface TaxCheckItem {
  form: string;
  code: string;
  message: string;
}
export interface TaxReportCheck {
  errors: TaxCheckItem[];
  warnings: TaxCheckItem[];
}

export const taxReportJ05 = {
  list: (params?: { organization_id?: string; limit?: number }) =>
    api.get<Page<TaxReport>>('/tax-report-j05', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<TaxReport>(`/tax-report-j05/${id}`).then((r) => r.data),
  create: (p: TaxReportCreate) =>
    api.post<TaxReport>('/tax-report-j05', p).then((r) => r.data),
  patch: (id: string, p: { document_date?: string }) =>
    api.patch<TaxReport>(`/tax-report-j05/${id}`, p).then((r) => r.data),
  preview: (id: string) =>
    api.get<TaxReportPreview>(`/tax-report-j05/${id}/preview`).then((r) => r.data),
  setLine: (id: string, line: {
    section: string; row_code: string; row_num?: number | null;
    value_text?: string | null;
  }) => api.post(`/tax-report-j05/${id}/lines`, line).then((r) => r.data),
  form: (id: string) =>
    api.get<{ forms: TaxFormView[] }>(`/tax-report-j05/${id}/form`).then((r) => r.data),
  check: (id: string) =>
    api.get<TaxReportCheck>(`/tax-report-j05/${id}/check`).then((r) => r.data),
  // «Зберегти» — чернетка розрахунку з'являється у Кабінеті ДПС (ідемпотентно).
  saveToCabinet: (id: string) =>
    api.post(`/tax-report-j05/${id}/save-to-cabinet`).then(() => undefined),
  files: (id: string) =>
    api.get<{ files: { fname: string; xml_base64: string }[] }>(`/tax-report-j05/${id}/files`).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/tax-report-j05/${id}`).then(() => undefined),
};
